import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { customerFeedback, subscribe, addCustomerFeedback } from '../../data/store';
import { MessageSquare, Search, Star, Clock, Calendar, AlignJustify, Calendar as CalendarIcon, LayoutGrid } from 'lucide-react';

export const CustomerFeedbackTab = ({ org, showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    const update = () => {
      setFeedback(customerFeedback.filter(f => f.orgId === org.id));
    };
    update();
    return subscribe(update);
  }, [org.id]);

  const filtered = feedback.filter(f => {
    const matchSearch = (f.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                        (f.phone || '').includes(searchTerm);
    return matchSearch;
  });

  return (
    <div className="flex flex-col gap-4 w-full">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shadow-sm">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search by client name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-md pl-9 pr-4 py-1.5 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-[var(--theme-tertiary)] text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                <th className="px-4 py-3 font-semibold">Client Name</th>
                <th className="px-4 py-3 font-semibold">Rating</th>
                <th className="px-4 py-3 font-semibold">Review</th>
                <th className="px-4 py-3 font-semibold text-right">Submitted On</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--theme-tertiary)] text-sm">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500 text-sm">
                    No feedback found.
                  </td>
                </tr>
              ) : (
                filtered.map((fb) => (
                  <tr key={fb.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{fb.customerName}</div>
                      <div className="text-xs text-slate-500">{fb.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5 text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} fill={i < fb.rating ? 'currentColor' : 'none'} className={i < fb.rating ? '' : 'text-slate-300'} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-700 text-sm max-w-md truncate" title={fb.comment}>
                        "{fb.comment}"
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <Clock size={12} />
                        {new Date(fb.date).toLocaleDateString()}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

CustomerFeedbackTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
